import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { db } from './firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  getDocs,
  // `doc` ölü dinleyiciyle birlikte düştü. (`updateDoc` zaten bu değişiklikten
  // ÖNCE de kullanılmıyordu — kapsam dışı, dokunulmadı.)
  updateDoc,
} from 'firebase/firestore';

// Masaüstü ve Mobil kartlar (Kural 6)
import Post from './Post';
import PostMobile from './PostMobile';

import ClipInFeed from './ClipInFeed';
// SPOTLIGHT — OneriSlider artık gerçek veriye bağlı (useSpotlight hook'u
// Firestore `spotlight/{city}` dokümanını okur; boşsa şerit demo'ya düşer).
import OneriSlider from './OneriSlider';
import FirstStepsCard from './components/FirstStepsCard';
import useSpotlight from './hooks/useSpotlight';
import usePullToRefresh from './hooks/usePullToRefresh';
import { showToast } from './ToastBoot';
import { getFollowingUids } from './services/follows';
import { gizliTakipGonderileriniGetir } from './services/akisGizliYazarlar';
import { useHiddenUids, filterByHidden } from './hooks/useHiddenUids';
import {
  buildReadyClipsForAuthorsQuery,
  fetchReadyClipsForAuthorsPage,
  IN_OPERATOR_MAX,
} from './services/clipsFeed';
import {
  getClipsFeedRankBy,
  getHomeFeedRankEnabled,
  getHomeFeedMaterializedEnabled,
  HOME_FEED_RANK_MAX_CHUNKS,
} from './constants/featureFlags';
import { fetchHomeFeedMaterializedPage } from './services/homeFeedMaterialized';
import { sortHomeFeed, buildFollowingOrder } from './services/homeFeedScore';
import type { HomeFeedItem } from './services/homeFeedScore';
import type { OneCikanOge } from './hooks/useSpotlight';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
// PR-1 — costMode: limited/emergency modlarında feed sayfa boyutları küçülür.
import useCostMode, { getCostModeConfig } from './hooks/useCostMode';
import './Feed.css';
import { IkonKoprusu } from "./icon-system/runtime/IkonKoprusu";

/* -----------------------------------------------------------
   EKRAN AYIRICI (JS tarafında — CSS @media değil)
   App.js ile aynı eşik: <= 768px → mobil
----------------------------------------------------------- */
function useIsMobile(maxWidth = 768) {
  const getMatch = () =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(max-width: ${maxWidth}px)`).matches
      : false;

  const [isMobile, setIsMobile] = useState(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);

    // ilk değer
    setIsMobile(mq.matches);

    // tarayıcı uyumluluğu
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, [maxWidth]);

  return isMobile;
}

/**
 * Akış ögesi = post VEYA clip belgesi (`{ ...d.data(), id, type }`).
 *
 * Şekil ELLE YAZILMADI: `HomeFeedItem` ZATEN var (`services/homeFeedScore`) ve
 * bu dosyanın sıraladığı şeyin sözleşmesi tam olarak o — `sortHomeFeed` onu
 * jenerik kısıt olarak alıyor. Yeni bir "FeedItem" tipi yazmak, bu oturumda
 * altı kez kapattığımız kopya-sözleşme sınıfını yeniden açardı.
 *
 * `& DocumentData`: ögeler HAM Firestore belgeleri ve `Post`/`PostMobile`/
 * `ClipInFeed` kartları serbest alanları okuyor. Yani tip, gövdenin bildiği
 * asgariyi söyler; bilmediğini yasaklamaz.
 */
type AkisOgesi = HomeFeedItem & DocumentData;

/** Sayfalama imleci — `onSnapshot`/`getDocs` son belgesi. */
type Imlec = QueryDocumentSnapshot<DocumentData> | null;

/** `loadMoreRank` içindeki paralel görevlerin sonucu. */
type SayfaSonucu = {
  kind: 'post' | 'clip';
  i: number;
  items: AkisOgesi[];
  lastDoc: Imlec;
  hasMore: boolean;
};

/** Rank modu sayfalayıcısı — eskiden yalnız bir YORUM satırında yazılıydı. */
type RankSayfalayici = {
  chunks: string[][];
  postCursors: Imlec[];
  postDone: boolean[];
  clipCursors: Imlec[];
  clipDone: boolean[];
};

// YENİ: onViewClip prop'u eklendi
// SPOTLIGHT: onOpenPlace — App'ten gelir (PlaceDetailModal açar). İsteğe bağlı:
// gelmezse place kartı haritaya/keşfe defansif fallback yapar.
function Feed({ onUserClick, onCommentClick, aktifKullaniciId, onViewClip, onOpenPlace }: {
  onUserClick?: (uid: string) => void;
  onCommentClick?: (post: AkisOgesi) => void;
  aktifKullaniciId?: string | null;
  onViewClip?: (clip: AkisOgesi) => void;
  onOpenPlace?: (place: { placeId: string; placeName: string } & Record<string, unknown>) => void;
}) {
  // SPOTLIGHT — Feed üstündeki "öne çıkanlar" şeridi gerçek veriye bağlı.
  // city şimdilik sabit 'milas'; doküman boşsa hook [] döner ve OneriSlider
  // kendi demo içeriğine düşer (mevcut görünüm korunur).
  const { items: spotlightItems, loading: spotlightLoading } = useSpotlight('milas');
  // Faz 1A Görev 6: post ve clip state'leri ayrıldı (feed = post ∪ clip canlı sayfa ∪ clip ek sayfalar)
  const [posts, setPosts] = useState<AkisOgesi[]>([]);
  // FAZ 0 — kendi postların ayrı sorgusu (gizli hesapta public filtreli
  // ana sorgudan düşer; bkz. aşağıdaki ownPostsQuery notu).
  const [ownPosts, setOwnPosts] = useState<AkisOgesi[]>([]);
  const [clipsPage, setClipsPage] = useState<AkisOgesi[]>([]); // ilk sayfa — onSnapshot ile canlı
  const [extraClips, setExtraClips] = useState<AkisOgesi[]>([]); // sonraki sayfalar — startAfter ile getDocs
  const [clipCursor, setClipCursor] = useState<Imlec>(null); // lastDoc of ilk sayfa + ek sayfaların sonuncusu
  const [hasMoreClips, setHasMoreClips] = useState(false);
  const [loadingMoreClips, setLoadingMoreClips] = useState(false);

  // ── RANK MODU sonsuz-kaydırma (FAZ D) ────────────────────────────────────
  // Rank modu ilk sayfayı chunk'lı çeker ve cursor pagination'ı kapatıyordu
  // (içerik tavanı ~3 chunk). Burada her chunk için post+clip cursor'ı tutup
  // "load more" ile DONMUŞ ilk sayfanın ALTINA yeni sayfa eklenir (IG deseni:
  // görünen sıra değişmez, yeni içerik kendi içinde skorlanıp aşağı eklenir).
  const [rankExtra, setRankExtra] = useState<AkisOgesi[]>([]); // eklenen sayfalar, kendi içinde sıralı
  const [hasMoreRank, setHasMoreRank] = useState(false);
  const [loadingMoreRank, setLoadingMoreRank] = useState(false);
  // Şekil artık YORUM değil TİP: `RankSayfalayici` (yukarıda).
  const rankPagerRef = useRef<RankSayfalayici | null>(null);

  // ── MATERIALIZED HOME FEED (FAZ D) ───────────────────────────────────────
  // matActive: null = henüz karar verilmedi (probe çalışıyor), true = materialized
  // feed aktif (kendi state/pagination'ını kullanır), false = fallback (mevcut
  // chunked-rank yolu çalışır). Flag OFF iken daima false.
  // ZERO-REGRESSION: flag off ise BAŞLANGIÇTA false ver → null→false geçişi
  // olmaz → chunked-rank effect (deps'inde matActive var) mount'ta İKİ KEZ
  // çalışıp listener'ları yeniden kurmaz (OFF yolu birebir eski davranış).
  const [matActive, setMatActive] = useState(() =>
    getHomeFeedMaterializedEnabled() ? null : false
  );
  const [matItems, setMatItems] = useState<AkisOgesi[]>([]); // karışık post+clip, ham şekil
  const [matCursor, setMatCursor] = useState<unknown>(null);
  const [hasMoreMat, setHasMoreMat] = useState(false);
  const [loadingMoreMat, setLoadingMoreMat] = useState(false);
  // FAZ 1 — "N yeni gönderi" pill. newCount: yüklemeden bu yana materialize
  // edilen yeni öğe sayısı; refreshNonce: pill'e basınca probe'u yeniden çalıştırır.
  const [newCount, setNewCount] = useState(0);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const matLoadedAtRef = useRef(0); // baseline: son yükleme zamanı (ms)

  const [loading, setLoading] = useState(true);
  // PR-2 (launch) — feed yükleme HATASI boş-feed'den ayrışır: yalnız gerçek
  // sorgu hatasında (ve hiçbir içerik yüklenemediyse) error state + Tekrar dene.
  const [loadError, setLoadError] = useState(false);
  const [takipEdilenler, setTakipEdilenler] = useState<string[]>([]);
  // Takip edilen GİZLİ hesapların gönderileri — yalnız fallback kolda dolar
  // (materialized kol bunları zaten hidratlıyor). Gerekçe: aşağıdaki
  // "İKİ KOL AYNI SÖZLEŞMEYE" effect'i.
  const [gizliPosts, setGizliPosts] = useState<AkisOgesi[]>([]);
  // ÖLÜ DİNLEYİCİ KALDIRILDI (kapı-daraltma turu): burada `aktifKullaniciInfo`
  // state'i ve onu besleyen `onSnapshot(users/{uid})` vardı. State dosyanın
  // HİÇBİR yerinde okunmuyordu → her feed mount'unda kalıcı bir profil
  // dinleyicisi açılıp sonucu çöpe atılıyordu. Burası ana ekran, yani en çok
  // mount edilen yer. Takip listesi bu dokümandan GELMİYOR
  // (services/follows.ts → users/{uid}/following ALT koleksiyonu), o yüzden
  // kaldırmak akış sorgularına dokunmuyor.

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const isMobile = useIsMobile(768);

  // PR-8 — mobil pull-to-refresh (window scroll; touch-only → desktop
  // etkilenmez). onRefresh mevcut refreshNonce mekanizmasını kullanır — pill
  // ve PR-2 retry ile AYNI yol: tüm feed modları yeniden kurulur, pagination
  // state'i her modun kendi effect'inde zaten sıfırlanır (yeni davranış yok).
  const ptr = usePullToRefresh({
    enabled: isMobile,
    onRefresh: () => {
      setNewCount(0);
      setRefreshNonce((n) => n + 1);
    },
  });

  // PR-1 — costMode'a göre sayfa boyutları (normal: 20/10, kısıtlı: 10/5).
  const costMode = useCostMode();
  const { feedPostsLimit, feedClipsPageSize } = getCostModeConfig(costMode);

  // Faz 1E Görev 3 — engellenen kullanıcıların içerikleri feed'e girmez.
  // Faz 1F Görev 4 — blocked + muted birleşik filtre.
  // `?? undefined`: prop `string | null | undefined`, tüketici `string |
  // undefined` istiyor. `null`u `undefined`a çevirmek davranış-nötr (ikisi de
  // "viewer yok" demek) ve tipi çağıranın gerçeğine uyduruyor.
  const { hiddenSet } = useHiddenUids({ viewerId: aktifKullaniciId ?? undefined });

  useEffect(() => {
    if (!aktifKullaniciId) return;

    // Takip edilen UID'leri subcollection (yeni) + /follows (eski) üzerinden yükle.
    // PERF (hız denetimi): `force:true` KALDIRILDI — her sekme dönüşünde tam
    // remount oluyor (App keep-alive yok) ve force her seferinde ağ turu
    // zorluyordu → feed sorgusu kurulmadan önce gecikme + "Akış Yükleniyor…".
    // follows servisinin 60sn modül-cache'i kullanılır → sekmeye dönünce
    // takip listesi anında bellekten gelir (takip/bırakma 60sn içinde yansır).
    getFollowingUids(aktifKullaniciId)
      .then((uids) => {
        setTakipEdilenler([aktifKullaniciId, ...uids]);
      })
      .catch(() => {
        // Hata durumunda sadece kendi içeriklerini göster
        setTakipEdilenler([aktifKullaniciId]);
      });
  }, [aktifKullaniciId]);

  useEffect(() => {
    if (takipEdilenler.length === 0) {
      setLoading(false);
      setPosts([]);
      setOwnPosts([]);
      setClipsPage([]);
      setExtraClips([]);
      setClipCursor(null);
      setHasMoreClips(false);
      setRankExtra([]);
      setHasMoreRank(false);
      rankPagerRef.current = null;
      return;
    }

    // FAZ D — Materialized feed aktif VEYA henüz karar verilmemişse (probe
    // çalışıyor) chunked-rank yüklemesini ATLA. Yalnız matActive===false
    // (flag off VEYA bu kullanıcı materialize-edilmemiş → fallback) iken
    // mevcut yol çalışır. Probe effect loading/state'i yönetir.
    if (getHomeFeedMaterializedEnabled() && matActive !== false) {
      return;
    }

    setLoading(true);

    // Takipçi listesi değiştiğinde ek sayfaları ve cursor'u sıfırla — aksi halde
    // eski yazarların clipleri yeni sorgularla karışabilir.
    setExtraClips([]);
    setClipCursor(null);
    setHasMoreClips(false);
    setRankExtra([]);
    setHasMoreRank(false);
    rankPagerRef.current = null;

    // ── ANA FEED RANKING (flag ON) — chunked getDocs, 30-takip cap'ini kaldırır
    // Default OFF → bu dal hiç çalışmaz, aşağıdaki onSnapshot yolu bugünküyle
    // BİREBİR aynıdır. ON iken: following 30'luk parçalara bölünür, paralel
    // getDocs ile çekilir; sıralama feedItems'ta client-side skorla yapılır.
    if (getHomeFeedRankEnabled()) {
      let cancelled = false;
      // PR-2 — sorgu hatası takibi: catch'ler boş sayfa döndürür (mevcut
      // davranış korunur) ama artık hata OLDUĞU kaydedilir; tümü boş +
      // hata varsa error state gösterilir (boş-feed maskesi yok).
      let anyQueryFailed = false;
      (async () => {
        const authorIds = takipEdilenler;
        // postları 30'luk parçalara böl (her parça bugünkü sorgu şekliyle aynı
        // → yeni index gerekmez), maliyet tavanı HOME_FEED_RANK_MAX_CHUNKS.
        const postChunks: string[][] = [];
        for (let i = 0; i < authorIds.length; i += IN_OPERATOR_MAX) {
          postChunks.push(authorIds.slice(i, i + IN_OPERATOR_MAX));
          if (postChunks.length >= HOME_FEED_RANK_MAX_CHUNKS) break;
        }
        const postPromise = Promise.all(
          postChunks.map((ids) =>
            getDocs(
              query(
                collection(db, 'posts'),
                where('authorId', 'in', ids),
                where('authorIsPrivate', '==', false),
                orderBy('tarih', 'desc'),
                limit(feedPostsLimit)
              )
            )
              .then((snap) => ({
                items: snap.docs.map((d) => ({ ...d.data(), id: d.id, type: 'post' })),
                lastDoc: snap.docs.length ? snap.docs[snap.docs.length - 1] : null,
                hasMore: snap.docs.length === feedPostsLimit,
              }))
              .catch(() => { anyQueryFailed = true; return { items: [], lastDoc: null, hasMore: false }; })
          )
        );
        // kendi postların (gizli hesapta public filtreli sorgudan düşer).
        const ownPromise = aktifKullaniciId
          ? getDocs(
              query(
                collection(db, 'posts'),
                where('authorId', '==', aktifKullaniciId),
                orderBy('tarih', 'desc'),
                limit(feedPostsLimit)
              )
            )
              .then((snap) =>
                snap.docs.map((d) => ({ ...d.data(), id: d.id, type: 'post' }))
              )
              .catch(() => { anyQueryFailed = true; return []; })
          : Promise.resolve([]);
        // Klipler de chunk başına SAYFALI çekilir (cursor pagination için
        // her chunk'ın lastDoc'u gerekir; fetchReadyClipsForAuthorsChunked
        // cursor döndürmüyordu).
        const clipsPromise = Promise.all(
          postChunks.map((ids) =>
            fetchReadyClipsForAuthorsPage({
              authorIds: ids,
              pageSize: feedClipsPageSize,
              cursorDoc: null,
              rankBy: 'tarih',
            }).catch(() => { anyQueryFailed = true; return { items: [], lastDoc: null, hasMore: false }; })
          )
        );

        try {
          const [postPages, ownP, clipPages] = await Promise.all([
            postPromise,
            ownPromise,
            clipsPromise,
          ]);
          if (cancelled) return;
          const flatPosts = ([] as AkisOgesi[]).concat(...postPages.map((p) => p.items));
          // chunk'lar ayrık yazar kümeleri → clip id çakışması olmaz; yine de
          // defansif dedup.
          const seenClip = new Set<string>();
          const flatClips: AkisOgesi[] = [];
          for (const page of clipPages) {
            for (const it of page.items) {
              if (it && it.id && !seenClip.has(it.id)) {
                seenClip.add(it.id);
                flatClips.push(it);
              }
            }
          }
          if (process.env.NODE_ENV !== 'production') {
            console.debug(
              `[feed] rank=on chunks=${postChunks.length} posts=${flatPosts.length} own=${ownP.length} clips=${flatClips.length}`
            );
          }
          setPosts(flatPosts);
          setOwnPosts(ownP);
          setClipsPage(flatClips);
          setHasMoreClips(false); // rank modunda legacy cursor pagination devre dışı
          // Rank pager state'i: her chunk'ın post+clip cursor'ı + tükendi mi.
          rankPagerRef.current = {
            chunks: postChunks,
            postCursors: postPages.map((p) => p.lastDoc),
            postDone: postPages.map((p) => !p.hasMore),
            clipCursors: clipPages.map((p) => p.lastDoc),
            clipDone: clipPages.map((p) => !p.hasMore),
          };
          setRankExtra([]);
          setHasMoreRank(
            postPages.some((p) => p.hasMore) || clipPages.some((p) => p.hasMore)
          );
          // PR-2 — yalnız GERÇEK hata + sıfır içerik → error state. Kısmi
          // başarıda (bazı chunk'lar geldi) içerik gösterilir, error basılmaz.
          setLoadError(
            anyQueryFailed &&
              flatPosts.length === 0 && ownP.length === 0 && flatClips.length === 0
          );
        } catch {
          /* hatalar zaten yutuldu; finally loading'i düşürür */
          if (!cancelled) setLoadError(true);
        } finally {
          // cancelled ise yeni effect kurulumu loading'i devralır (sağlam desen).
          if (!cancelled) setLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    // Firestore 'in' operatörü maks 30 (helper da aynı cap'i uyguluyor).
    const queryIds = takipEdilenler.slice(0, IN_OPERATOR_MAX);

    // FAZ 0 (Sosyal Grafik Güvenlik) — posts LIST kuralı artık gizli içerik
    // koruması yapıyor; in-sorgusu rules'tan geçmek için public filtresi
    // taşımak ZORUNDA (30 yazarlık in-sorgusunda yazar başına exists()
    // bütçesi aşılıyor — emulator ölçümü). Trade-off: gizli hesapların
    // postları ana feed'de görünmez, profilden erişilir (takipçiyse).
    // Faz 3'te materialized following-feed bu kısıtı kaldıracak.
    const postsQuery = query(
      collection(db, 'posts'),
      where('authorId', 'in', queryIds),
      where('authorIsPrivate', '==', false),
      orderBy('tarih', 'desc'),
      limit(feedPostsLimit)
    );
    // Faz 1A Görev 6: SADECE status == "ready" clipler feed'e girer.
    // İşlenen (processing) veya hatalı (failed/rejected) clipler görünmez →
    // kullanıcı kırık manifest/player ile karşılaşmaz.
    // Faz 1B Görev 4: sıralama feature flag ile ("tarih" | "engagementScore").
    const rankBy = getClipsFeedRankBy();
    const clipsQuery = buildReadyClipsForAuthorsQuery({
      authorIds: queryIds,
      pageSize: feedClipsPageSize,
      cursorDoc: null,
      rankBy,
    });

    const unsubscribePosts = onSnapshot(
      postsQuery,
      (postsSnapshot) => {
        setPosts(
          postsSnapshot.docs.map((d) => ({ ...d.data(), id: d.id, type: 'post' }))
        );
        setLoading(false);
        setLoadError(false);
      },
      (err) => {
        // PR-2 — legacy modda error callback YOKTU → listener sessizce ölüyor,
        // loading sonsuza asılı kalıyordu (sonsuz "Akış Yükleniyor…").
        console.error('[Feed] posts listener error:', err?.code || err?.message || err);
        setLoading(false);
        setLoadError(true);
      }
    );

    // FAZ 0 — kendi postların ayrı sorguyla gelir: viewer gizli hesapsa
    // kendi içeriği authorIsPrivate=true olur ve üstteki public filtreli
    // sorgudan düşer; authorId==uid sorgusu rules'tan owner olarak geçer.
    const ownPostsQuery = aktifKullaniciId
      ? query(
          collection(db, 'posts'),
          where('authorId', '==', aktifKullaniciId),
          orderBy('tarih', 'desc'),
          limit(feedPostsLimit)
        )
      : null;
    const unsubscribeOwnPosts = ownPostsQuery
      ? onSnapshot(ownPostsQuery, (snap) => {
          setOwnPosts(
            snap.docs.map((d) => ({ ...d.data(), id: d.id, type: 'post' }))
          );
        })
      : () => {};

    const unsubscribeClips = clipsQuery
      ? onSnapshot(
          clipsQuery,
          (clipsSnapshot) => {
            const docs = clipsSnapshot.docs;
            setClipsPage(
              docs.map((d) => ({ ...d.data(), id: d.id, type: 'clip' }))
            );
            const last = docs.length > 0 ? docs[docs.length - 1] : null;
            setClipCursor(last);
            setHasMoreClips(docs.length === feedClipsPageSize);
            setLoading(false);
          },
          (err) => {
            // PR-2 — clips listener hatası da loading'i düşürür (posts listener
            // sağsa içerik yine akar; loadError'ı posts belirler).
            console.error('[Feed] clips listener error:', err?.code || err?.message || err);
            setLoading(false);
          }
        )
      : () => {};

    return () => {
      unsubscribePosts();
      unsubscribeOwnPosts();
      unsubscribeClips();
    };
    // PR-2 — refreshNonce: error state'teki "Tekrar dene" bu effect'i de
    // yeniden kurar (materialized probe zaten nonce'a bağlıydı).
  }, [takipEdilenler, aktifKullaniciId, feedPostsLimit, feedClipsPageSize, matActive, refreshNonce]);

  // ── İKİ KOL AYNI SÖZLEŞMEYE (2026-08-25, ölçülerek) ────────────────────
  // ÖLÇÜLEN KUSUR: materialized kol takip edilen GİZLİ hesabın gönderisini
  // GÖSTERİYOR (per-doc getDoc + rules), fallback kol GÖSTERMİYORDU (tüm
  // sorguları `authorIsPrivate == false` taşır). Yani aynı kullanıcı, aynı
  // `/` rotası → hangi kolun aktif olduğuna göre İKİ FARKLI AKIŞ; ayrım
  // `rawCount === 0` ile sessizce kullanıcıdan kullanıcıya değişiyordu.
  //
  // YÖN (tercih değil, `firestore.rules`tan okundu): `posts allow list`
  //   authorIsPrivate != true || canReadPrivateContent(authorId)
  // ve `canReadPrivateContent` onaylı takipçi edge'ini kabul ediyor →
  // takipçinin gizli yazarın gönderisini görmesi MEŞRU → materialized kol
  // doğru, fallback kol eksik. Fallback'in filtresi bir güvenlik kısıtı
  // değil, bu dosyanın kendi yorumunda yazılı bir MALİYET kısıtı.
  //
  // Bu effect fallback kolun HER İKİ alt yolunu da (rank ON ve OFF) kapatır
  // — ikisi de aynı public filtresini taşıyor. Toplu `in` sorgusu
  // DEĞİŞTİRİLMEDİ (bütçe gerekçesi duruyor); eksik gönderiler yazar
  // başına tek sorguyla EKLENİR.
  //
  // ⚠️ Klipler bilerek kapsam dışı: `clips allow list` takipçiye KAPALI
  // (kasıtlı anti-enumeration; kuralın kendi yorumu materialized feed'i
  // tek meşru yol olarak gösteriyor). Denemek sorgunun tamamını reddettirir.
  // Ayrıntı + index ölçümü: `services/akisGizliYazarlar.ts`.
  useEffect(() => {
    if (!aktifKullaniciId || takipEdilenler.length === 0) {
      setGizliPosts([]);
      return;
    }
    // Materialized kol aktif (veya probe henüz karar vermedi) → o kol bu
    // içeriği ZATEN gösteriyor; burada çekmek çift kayıt olurdu.
    if (getHomeFeedMaterializedEnabled() && matActive !== false) {
      setGizliPosts([]);
      return;
    }
    let iptal = false;
    // Fail-safe: kural/index/kota ne dönerse dönsün boş dizi → bugünkü akış
    // aynen kalır, yalnız gizli-takip gönderileri eklenemez.
    gizliTakipGonderileriniGetir(takipEdilenler, aktifKullaniciId)
      .then((ogeler) => {
        if (!iptal) setGizliPosts(ogeler as AkisOgesi[]);
      })
      .catch(() => {
        if (!iptal) setGizliPosts([]);
      });
    return () => {
      iptal = true;
    };
  }, [takipEdilenler, aktifKullaniciId, matActive, refreshNonce]);

  // FAZ D — Materialized feed probe. Flag ON iken users/{uid}/followingFeed'den
  // 1. sayfayı + kendi son post/kliplerini çeker. rawCount===0 → bu kullanıcı
  // materialize edilmemiş → matActive=false (fallback: chunked-rank yolu).
  // Aksi halde matActive=true; materialized kendi state/pagination'ını kullanır.
  useEffect(() => {
    if (!getHomeFeedMaterializedEnabled()) {
      setMatActive(false); // flag off → daima fallback (mevcut davranış)
      return;
    }
    if (takipEdilenler.length === 0 || !aktifKullaniciId) {
      setMatActive(false);
      return;
    }
    let cancelled = false;
    // Refresh (refreshNonce) sırasında matActive=true KALSIN (null flash yok →
    // mevcut feed görünür kalır); yalnız ilk karar/aday-değişiminde null yap.
    setMatActive((cur) => (cur === true ? true : null));
    setLoading((cur) => cur);
    (async () => {
      try {
        const [page, ownPostsSnap, ownClips] = await Promise.all([
          fetchHomeFeedMaterializedPage({
            uid: aktifKullaniciId,
            pageSize: feedClipsPageSize,
          }),
          getDocs(
            query(
              collection(db, 'posts'),
              where('authorId', '==', aktifKullaniciId),
              orderBy('tarih', 'desc'),
              limit(feedPostsLimit)
            )
          ).catch(() => null),
          fetchReadyClipsForAuthorsPage({
            authorIds: [aktifKullaniciId],
            pageSize: feedClipsPageSize,
            rankBy: 'tarih',
          }).catch(() => ({ items: [] })),
        ]);
        if (cancelled) return;
        if (!page || page.rawCount === 0) {
          // Materialize edilmemiş → mevcut chunked-rank yoluna düş.
          setMatActive(false);
          return;
        }
        // Kendi içeriğin (materialized feed self'i hariç tutar) + materialized.
        const own = [];
        if (ownPostsSnap) {
          for (const d of ownPostsSnap.docs) {
            own.push({ ...d.data(), id: d.id, type: 'post' });
          }
        }
        for (const c of ownClips?.items || []) own.push(c);
        const seen = new Set();
        const merged = [];
        for (const it of [...own, ...page.items]) {
          if (!it || !it.id) continue;
          const key = `${it.type}:${it.id}`;
          if (seen.has(key)) continue;
          seen.add(key);
          merged.push(it);
        }
        setMatItems(merged);
        setMatCursor(page.nextCursor || null);
        setHasMoreMat(!!page.hasMore);
        setMatActive(true);
        // FAZ 1 — yeni-gönderi baseline'ı: bu yüklemeden SONRA materialize
        // edilenler "yeni" sayılır.
        matLoadedAtRef.current = Date.now();
        setNewCount(0);
      } catch (err) {
        if (cancelled) return;
        console.error('[Feed] materialized probe hatası:', err);
        setMatActive(false); // hata → güvenli fallback
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [takipEdilenler, aktifKullaniciId, feedPostsLimit, feedClipsPageSize, refreshNonce]);

  // FAZ 1 — yeni-gönderi sayacı listener'ı. materialized aktifken
  // users/{uid}/followingFeed'i addedAt DESC izler; baseline'dan sonra
  // materialize edilen öğeleri sayar (limit 10 → "9+"). Tek limit'li listener,
  // maliyet ihmal edilebilir. Gate: matActive===true.
  useEffect(() => {
    if (matActive !== true || !aktifKullaniciId) return;
    const q = query(
      collection(db, 'users', aktifKullaniciId, 'followingFeed'),
      orderBy('addedAt', 'desc'),
      limit(10)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const base = matLoadedAtRef.current || 0;
        if (!base) return;
        let n = 0;
        snap.forEach((d) => {
          const ms = d.data()?.addedAt?.toMillis?.() || 0;
          if (ms > base) n += 1;
        });
        setNewCount(n);
      },
      () => {}
    );
    return () => { try { unsub(); } catch (_) {} };
  }, [matActive, aktifKullaniciId]);

  // Materialized feed load-more: sonraki followingFeed sayfasını çek, kendi
  // içinde skorlanmış olarak listenin altına ekle (dedup).
  const loadMoreMat = useCallback(async () => {
    if (loadingMoreMat || !hasMoreMat || !matCursor || !aktifKullaniciId) return;
    setLoadingMoreMat(true);
    try {
      const page = await fetchHomeFeedMaterializedPage({
        uid: aktifKullaniciId,
        cursor: matCursor,
        pageSize: feedClipsPageSize,
      });
      if (page.items.length > 0) {
        setMatItems((prev) => {
          const seen = new Set(prev.map((x) => `${x.type}:${x.id}`));
          return [
            ...prev,
            ...page.items.filter((x) => x && x.id && !seen.has(`${x.type}:${x.id}`)),
          ];
        });
      }
      setMatCursor(page.nextCursor || null);
      setHasMoreMat(!!page.hasMore);
    } catch (err) {
      console.error('[Feed] loadMoreMat hatası:', err);
    } finally {
      setLoadingMoreMat(false);
    }
  }, [loadingMoreMat, hasMoreMat, matCursor, aktifKullaniciId, feedClipsPageSize]);

  // Post + clip (canlı + ek) birleştir, tarih desc sırala (tekillik: clip id).
  // Faz 1E Görev 3: engellenen yazarların postu/klibi çıkarılır.
  const feedItems = useMemo(() => {
    // FAZ D — Materialized feed aktifse render bu listeden gelir. matItems
    // zaten followingFeed clipTarih DESC sıralı (+ kendi içeriğin başta);
    // rank flag'i açıksa skor uygula, engellenenleri filtrele.
    if (matActive === true) {
      const ordered = getHomeFeedRankEnabled()
        ? sortHomeFeed(matItems, {
            nowMs: Date.now(),
            ownUid: aktifKullaniciId,
            followingOrder: buildFollowingOrder(takipEdilenler),
            followCount: takipEdilenler.length,
          })
        : matItems;
      return filterByHidden(ordered, (it: AkisOgesi) => it.authorId, hiddenSet);
    }
    const seenClipIds = new Set<string>();
    const clips: AkisOgesi[] = [];
    for (const c of [...clipsPage, ...extraClips]) {
      if (c && c.id && !seenClipIds.has(c.id)) {
        seenClipIds.add(c.id);
        clips.push(c);
      }
    }
    // tarih.seconds null olabilir (serverTimestamp optimistic update — sunucu henüz yazmadı).
    // Bu durumda postu çıkarma; sıralama için şimdiki zamanı fallback olarak kullan.
    const toSeconds = (it: AkisOgesi) => {
      if (!it.tarih) return Date.now() / 1000;
      if (typeof it.tarih.seconds === 'number') return it.tarih.seconds;
      if (it.tarih instanceof Date) return it.tarih.getTime() / 1000;
      if (typeof it.tarih === 'string') return new Date(it.tarih).getTime() / 1000;
      return Date.now() / 1000;
    };
    // Kendi postların + takip edilen public postlar + takip edilen GİZLİ
    // hesapların postları (id dedup — kendi postların açık hesapta her iki
    // sorgudan da gelebilir; gizli kol da aynı postu getirebilir).
    const seenPostIds = new Set<string>();
    const allPosts: AkisOgesi[] = [];
    for (const p of [...posts, ...ownPosts, ...gizliPosts]) {
      if (p && p.id && !seenPostIds.has(p.id)) {
        seenPostIds.add(p.id);
        allPosts.push(p);
      }
    }
    // Sıralama: flag ON → client-side skor (recency×engagement×yakınlık),
    // OFF → bugünkü saf tarih desc (BİREBİR aynı).
    const combined = [...allPosts, ...clips];
    const ordered = getHomeFeedRankEnabled()
      ? sortHomeFeed(combined, {
          nowMs: Date.now(),
          ownUid: aktifKullaniciId,
          followingOrder: buildFollowingOrder(takipEdilenler),
          followCount: takipEdilenler.length,
        })
      : combined.sort((a, b) => toSeconds(b) - toSeconds(a));
    // Rank load-more sayfaları DONMUŞ ilk sayfanın altına eklenir (IG deseni:
    // görünen sıra değişmez). rankExtra kendi içinde zaten skorlu; ilk sayfada
    // görünenleri (type:id) dedup'la.
    let withExtra = ordered;
    if (getHomeFeedRankEnabled() && rankExtra.length) {
      const seenKey = new Set(ordered.map((it) => `${it.type}:${it.id}`));
      const appended = rankExtra.filter(
        (it) => it && it.id && !seenKey.has(`${it.type}:${it.id}`)
      );
      if (appended.length) withExtra = [...ordered, ...appended];
    }
    return filterByHidden(withExtra, (it: AkisOgesi) => it.authorId, hiddenSet);
  }, [posts, ownPosts, gizliPosts, clipsPage, extraClips, rankExtra, matActive, matItems, hiddenSet, takipEdilenler, aktifKullaniciId]);

  // Rank modu sonsuz-kaydırma: her chunk için bir sonraki post+clip sayfasını
  // startAfter ile çek, yeni gelenleri kendi içinde skorla, listenin ALTINA ekle.
  const loadMoreRank = useCallback(async () => {
    // `any` DEĞİL: `rankPagerRef` artık `RankSayfalayici | null` — `any`
    // yazmak az önce kurduğum tipi geçersiz kılardı (hata örtücüsünün en
    // sinsi hâli: tip VAR ama okunmuyor).
    const pager = rankPagerRef.current;
    if (loadingMoreRank || !hasMoreRank || !pager) return;
    setLoadingMoreRank(true);
    try {
      const { chunks, postCursors, postDone, clipCursors, clipDone } = pager;
      const tasks: Promise<SayfaSonucu>[] = [];
      chunks.forEach((ids: string[], i: number) => {
        if (!postDone[i] && postCursors[i]) {
          tasks.push(
            getDocs(
              query(
                collection(db, 'posts'),
                where('authorId', 'in', ids),
                where('authorIsPrivate', '==', false),
                orderBy('tarih', 'desc'),
                startAfter(postCursors[i]),
                limit(feedPostsLimit)
              )
            )
              .then((snap) => ({
                kind: 'post' as const,
                i,
                items: snap.docs.map((d) => ({ ...d.data(), id: d.id, type: 'post' })),
                lastDoc: snap.docs.length ? snap.docs[snap.docs.length - 1] : null,
                hasMore: snap.docs.length === feedPostsLimit,
              }))
              .catch(() => ({ kind: 'post' as const, i, items: [], lastDoc: null, hasMore: false }))
          );
        }
        if (!clipDone[i] && clipCursors[i]) {
          tasks.push(
            fetchReadyClipsForAuthorsPage({
              authorIds: ids,
              pageSize: feedClipsPageSize,
              cursorDoc: clipCursors[i],
              rankBy: 'tarih',
            })
              .then((page) => ({ kind: 'clip' as const, i, items: page.items, lastDoc: page.lastDoc, hasMore: page.hasMore }))
              .catch(() => ({ kind: 'clip' as const, i, items: [], lastDoc: null, hasMore: false }))
          );
        }
      });
      if (tasks.length === 0) {
        setHasMoreRank(false);
        return;
      }
      const results = await Promise.all(tasks);
      const fresh: AkisOgesi[] = [];
      for (const r of results) {
        if (r.kind === 'post') {
          pager.postCursors[r.i] = r.lastDoc || pager.postCursors[r.i];
          pager.postDone[r.i] = !r.hasMore;
        } else {
          pager.clipCursors[r.i] = r.lastDoc || pager.clipCursors[r.i];
          pager.clipDone[r.i] = !r.hasMore;
        }
        for (const it of r.items) fresh.push(it);
      }
      if (fresh.length) {
        const orderedNew = getHomeFeedRankEnabled()
          ? sortHomeFeed(fresh, {
              nowMs: Date.now(),
              ownUid: aktifKullaniciId,
              followingOrder: buildFollowingOrder(takipEdilenler),
              followCount: takipEdilenler.length,
            })
          : fresh;
        setRankExtra((prev) => {
          const seen = new Set(prev.map((x) => `${x.type}:${x.id}`));
          return [
            ...prev,
            ...orderedNew.filter((x) => x && x.id && !seen.has(`${x.type}:${x.id}`)),
          ];
        });
      }
      const stillMore =
        pager.postDone.some((d: boolean) => !d) || pager.clipDone.some((d: boolean) => !d);
      setHasMoreRank(stillMore);
    } catch (err) {
      console.error('[Feed] loadMoreRank hatası:', err);
    } finally {
      setLoadingMoreRank(false);
    }
  }, [
    loadingMoreRank,
    hasMoreRank,
    feedPostsLimit,
    feedClipsPageSize,
    aktifKullaniciId,
    takipEdilenler,
  ]);

  // Cursor pagination: sonraki sayfayı startAfter ile çek.
  const loadMoreClips = useCallback(async () => {
    if (loadingMoreClips || !hasMoreClips || !clipCursor) return;
    const queryIds = takipEdilenler.slice(0, IN_OPERATOR_MAX);
    if (queryIds.length === 0) return;
    setLoadingMoreClips(true);
    try {
      const page = await fetchReadyClipsForAuthorsPage({
        authorIds: queryIds,
        pageSize: feedClipsPageSize,
        cursorDoc: clipCursor,
        rankBy: getClipsFeedRankBy(),
      });
      if (page.items.length > 0) {
        setExtraClips((prev) => {
          const seen = new Set(prev.map((c) => c.id));
          // clipsPage ile de çakışmayı önle
          for (const c of clipsPage) seen.add(c.id);
          return [...prev, ...page.items.filter((c) => !seen.has(c.id))];
        });
      }
      setClipCursor(page.lastDoc || clipCursor);
      setHasMoreClips(page.hasMore);
    } catch (err) {
      console.error('[Feed] loadMoreClips hatası:', err);
    } finally {
      setLoadingMoreClips(false);
    }
  }, [loadingMoreClips, hasMoreClips, clipCursor, takipEdilenler, clipsPage, feedClipsPageSize]);

  // Sentinel IntersectionObserver — feed altına yaklaşınca sonraki sayfa çek.
  // Materialized → loadMoreMat; rank modu → loadMoreRank; legacy → loadMoreClips.
  useEffect(() => {
    const mat = matActive === true;
    const rank = getHomeFeedRankEnabled();
    const active = mat ? hasMoreMat : rank ? hasMoreRank : hasMoreClips;
    if (!active) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (mat) loadMoreMat();
          else if (rank) loadMoreRank();
          else loadMoreClips();
        }
      },
      { rootMargin: '600px 0px' } // ekranın altına yaklaşırken preload
    );
    io.observe(el);
    return () => io.disconnect();
  }, [matActive, hasMoreMat, loadMoreMat, hasMoreClips, hasMoreRank, loadMoreClips, loadMoreRank]);

  // PR-29 — KİLİTLİ ÜRÜN KURALI: ikili beğeni (begenenler yazımı) KALDIRILDI.
  // Tek engagement sinyali 5★ — kartlar StarRatingV2/rateContent kullanır.
  // (Eski merkezi handleLike + notifyLike zinciri burada dururdu.)

  // PR-2 — premium ilk-izlenim: metin yerine IG-paritesi post-kart skeleton'ı.
  // Aynı .feed-container genişliği → içerik gelince layout ZIPLAMAZ.
  if (loading)
    return (
      <div className="feed-container" aria-busy="true" aria-label="Akış yükleniyor">
        {[0, 1, 2].map((i) => (
          <div className="feed-skel-card" key={i}>
            <div className="feed-skel-head">
              <div className="feed-skel-avatar feed-skel-shimmer" />
              <div className="feed-skel-headlines">
                <div className="feed-skel-line feed-skel-shimmer" style={{ width: '38%' }} />
                <div className="feed-skel-line feed-skel-shimmer" style={{ width: '22%' }} />
              </div>
            </div>
            <div className="feed-skel-media feed-skel-shimmer" />
            <div className="feed-skel-foot">
              <div className="feed-skel-line feed-skel-shimmer" style={{ width: '30%' }} />
              <div className="feed-skel-line feed-skel-shimmer" style={{ width: '64%' }} />
            </div>
          </div>
        ))}
      </div>
    );

  // SPOTLIGHT navigasyonu — her kart tipi bir aksiyona bağlanır (dead tap yok).
  const handleSpotlightProfile = (it?: OneCikanOge) => {
    // item.uid veya refId profil UID'si olabilir.
    const uid = it?.uid || it?.refId || it?.id;
    if (uid && typeof onUserClick === 'function') {
      onUserClick(uid);
    } else {
      showToast('Profil açılamadı', { variant: 'error' });
    }
  };
  const handleSpotlightPlace = (it?: OneCikanOge) => {
    // PlaceDetailModal `placeData.placeId` + `placeData.placeName` bekler;
    // refId/placeId/id ve name/title alanlarını modal şemasına eşle.
    const placeId = it?.placeId || it?.refId || it?.id;
    if (placeId && typeof onOpenPlace === 'function') {
      onOpenPlace({
        placeId,
        placeName: it?.placeName || it?.name || it?.title || '',
        ...(it?.place || {}),
      });
    } else if (it?.link || it?.url) {
      // placeId yoksa harita/dış bağlantıya güvenli yönlendirme.
      if (!openSafeUrl(it.link || it.url)) showToast('Mekânı haritada keşfet', {});
    } else {
      // App place handler'ı geçmediyse defansif: kullanıcı bilgilendirilir.
      showToast('Mekânı haritada keşfet', {});
    }
  };
  // Güvenli dış link açıcı — yalnız https; tabnabbing'e karşı noopener.
  const openSafeUrl = (raw?: unknown) => {
    try {
      const u = new URL(String(raw || ''));
      if (u.protocol !== 'https:') return false;
      window.open(u.href, '_blank', 'noopener,noreferrer');
      return true;
    } catch { return false; }
  };
  const handleSpotlightNews = (it?: OneCikanOge) => {
    if (!openSafeUrl(it?.link || it?.url)) {
      showToast('Haber bağlantısı geçersiz', { variant: 'error' });
    }
  };
  const handleSpotlightEvent = (it?: OneCikanOge) => {
    if ((it?.link || it?.url) && openSafeUrl(it.link || it.url)) return;
    if (it?.placeId || it?.refId) return handleSpotlightPlace(it);
    showToast('Etkinlikler çok yakında', {});
  };
  const handleSpotlightInfo = (it?: OneCikanOge) => {
    if ((it?.link || it?.url) && openSafeUrl(it.link || it.url)) return;
    showToast(it?.toast || 'Çok yakında ✨', {});
  };

  return (
    <div className="feed-container">
      {/* PR-8 — mobil pull-to-refresh göstergesi (touch-only). */}
      {isMobile && ptr.visible ? (
        <div
          className={`ptr-indicator${ptr.refreshing ? ' ptr-indicator--spin' : ''}${ptr.armed ? ' ptr-indicator--armed' : ''}`}
          style={ptr.refreshing ? undefined : { transform: `translateX(-50%) translateY(${Math.round(ptr.pullPx * 0.5)}px)` }}
          aria-hidden="true"
        >
          <span className="ptr-indicator__dot" />
        </div>
      ) : null}
      {/* FAZ 1 — "N yeni gönderi" pill (IG paritesi). Materialized aktif +
          baseline'dan sonra yeni içerik varsa: dokun → en üste kaydır + yenile. */}
      {matActive === true && newCount > 0 ? (
        <button
          className="pressable"
          type="button"
          onClick={() => {
            try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (_) {}
            setNewCount(0);
            setRefreshNonce((n) => n + 1);
          }}
          style={{
            position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 64px)',
            left: '50%', transform: 'translateX(-50%)', zIndex: 50,
            background: 'var(--accent, var(--app-accent))', color: '#fff', border: 'none',
            borderRadius: 999, padding: '8px 16px', fontSize: 14, fontWeight: 600,
            boxShadow: '0 2px 10px rgba(0,0,0,0.25)', cursor: 'pointer',
          }}
        >
          ↑ {newCount >= 10 ? '9+' : newCount} yeni gönderi
        </button>
      ) : null}
      <OneriSlider
        items={spotlightItems}
        loading={spotlightLoading}
        onOpenProfile={handleSpotlightProfile}
        onOpenPlace={handleSpotlightPlace}
        onOpenEvent={handleSpotlightEvent}
        onOpenNews={handleSpotlightNews}
        onOpenInfo={handleSpotlightInfo}
        autoplayMs={2800}
      />

      {/* EMİR #78 — İlk Adımlar kartı (yeni kullanıcı döngü nudge'ı; kapatılabilir). */}
      <FirstStepsCard
        uid={aktifKullaniciId ?? undefined}
        onCompose={() => { try { window.dispatchEvent(new CustomEvent("mlv3:open-compose")); } catch (_) {} }}
      />

      {/* PR-2 — HATA ≠ BOŞ: gerçek sorgu hatasında (ve içerik yokken) error
          state + Tekrar dene; boş feed yalnız hatasız gerçek boşlukta. */}
      {loadError && feedItems.length === 0 && !loading && (
        <div className="feed-state feed-state--error" role="alert">
          <div className="feed-state__icon" aria-hidden="true"><IkonKoprusu aile="system.warning" boyut="lg" eski="⚠️" /></div>
          <h2 className="feed-state__title">Akış yüklenemedi</h2>
          <p className="feed-state__text">Bağlantını kontrol edip tekrar dene.</p>
          <button
            type="button"
            className="feed-state__btn"
            onClick={() => {
              setLoadError(false);
              setLoading(true);
              setRefreshNonce((n) => n + 1);
            }}
          >
            Tekrar dene
          </button>
        </div>
      )}
      {!loadError && feedItems.length === 0 && !loading && (
        <div className="feed-state feed-state--empty">
          <div className="feed-state__icon" aria-hidden="true"><IkonKoprusu aile="navigation.discovery" boyut="lg" eski="✨" /></div>
          <h2 className="feed-state__title">Akışın boş görünüyor</h2>
          <p className="feed-state__text">
            Takip ettiğin kişilerin gönderileri burada görünecek.
          </p>
        </div>
      )}

      <div>
        {feedItems.map((item: AkisOgesi) => {
          if (item.type === 'post') {
            const Card = isMobile ? PostMobile : Post;
            return (
              <Card
                key={`post-${item.id}`}
                post={item}
                aktifKullaniciId={aktifKullaniciId}
                onUserClick={onUserClick}
                onCommentClick={onCommentClick}
              />
            );
          }
          if (item.type === 'clip') {
            return (
              <ClipInFeed
                key={`clip-${item.id}`}
                clip={item}
                aktifKullaniciId={aktifKullaniciId}
                onUserClick={onUserClick}
                onViewClip={onViewClip} // YENİ: Fonksiyonu iletiyoruz
              />
            );
          }
          return null;
        })}

        {/* Sayfa sentinel — görünür olunca sonraki sayfa çekilir. Rank modu
            (hasMoreRank) ve legacy cursor modu (hasMoreClips) ortak kullanır. */}
        {(hasMoreClips || hasMoreRank || hasMoreMat) && (
          <div
            ref={sentinelRef}
            aria-hidden="true"
            style={{ height: 1, width: '100%' }}
          />
        )}
        {(loadingMoreClips || loadingMoreRank || loadingMoreMat) && (
          <p style={{ textAlign: 'center', color: '#8e8e8e', padding: '12px 0' }}>
            Daha fazla içerik yükleniyor…
          </p>
        )}
      </div>
    </div>
  );
}

export default Feed;
